// src/contracts/approvals.contract.ts
import { initContract } from "@ts-rest/core";
import { z as z3 } from "zod";

// src/schemas/approvals.ts
import { z } from "zod";
var SigningStatusSchema = z.enum([
  "PENDING",
  "COLLECTING_SIGNATURES",
  "PARTIALLY_SIGNED",
  "FULLY_SIGNED",
  "AWAITING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "EXECUTED",
  "EXPIRED",
  "SIGNING_FAILED"
]);
var ApprovalDecisionSchema = z.enum(["APPROVED", "REJECTED"]);
var SigningDepartmentSchema = z.enum([
  "origination",
  "risk",
  "compliance",
  "operations"
]);
var SigningPolicyIdSchema = z.enum([
  "standard-event",
  "loan-lifecycle",
  "high-value-transaction",
  "manual-override"
]);
var DepartmentSignatureSchema = z.object({
  department: SigningDepartmentSchema,
  signerAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  r: z.string().regex(/^0x[a-fA-F0-9]{64}$/),
  s: z.string().regex(/^0x[a-fA-F0-9]{64}$/),
  v: z.number().int().min(0).max(255),
  signature: z.string().regex(/^0x[a-fA-F0-9]+$/),
  signedAt: z.string().datetime()
});
var PendingSignatureSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  eventHash: z.string().regex(/^0x[a-fA-F0-9]{64}$/),
  eventType: z.string(),
  policyId: SigningPolicyIdSchema,
  requiredDepartments: z.array(SigningDepartmentSchema),
  threshold: z.number().int().min(1).max(4),
  status: SigningStatusSchema,
  expiresAt: z.string().datetime(),
  requiresHumanApproval: z.boolean(),
  approvalDeadline: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var SigningWorkflowResultSchema = z.object({
  pendingId: z.string(),
  eventId: z.string(),
  status: SigningStatusSchema,
  policyId: SigningPolicyIdSchema,
  pendingDepartments: z.array(SigningDepartmentSchema),
  completedDepartments: z.array(SigningDepartmentSchema),
  signatures: z.array(DepartmentSignatureSchema),
  expiresAt: z.string().datetime(),
  aggregatedSignature: z.string().regex(/^0x[a-fA-F0-9]+$/).optional(),
  error: z.string().optional()
});
var HumanApprovalRecordSchema = z.object({
  id: z.string(),
  pendingSignatureId: z.string(),
  approverId: z.string(),
  approverEmail: z.string().email().nullable(),
  decision: ApprovalDecisionSchema,
  reason: z.string().nullable(),
  decidedAt: z.string().datetime()
});
var SubmitApprovalSchema = z.object({
  decision: ApprovalDecisionSchema,
  reason: z.string().max(500).optional()
}).strict();
var ApprovalsQuerySchema = z.object({
  status: SigningStatusSchema.optional(),
  policyId: SigningPolicyIdSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});
var SignaturesQuerySchema = z.object({
  eventId: z.string().optional(),
  status: SigningStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});

// src/schemas/common.ts
import { z as z2 } from "zod";
var EthereumAddressSchema = z2.string().regex(/^0x[a-fA-F0-9]{40}$/, "Invalid Ethereum address");
var TxHashSchema = z2.string().regex(/^0x[0-9a-f]{64}$/, "Invalid transaction hash");
var CuidSchema = z2.string().min(1);
var DecimalStringSchema = z2.string().regex(/^\d+$/, "Must be a positive integer string");
var StateCodeSchema = z2.string().length(2).toUpperCase();
var ZipCodeSchema = z2.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format");
var InterestRateBasisPointsSchema = z2.number().int().min(0).max(1e4);
var TermMonthsSchema = z2.number().int().min(1).max(600);
var PaginationQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  limit: z2.coerce.number().int().min(1).max(100).default(20)
});
var PaginatedResponseSchema = (itemSchema) => z2.object({
  items: z2.array(itemSchema),
  total: z2.number(),
  page: z2.number(),
  limit: z2.number(),
  totalPages: z2.number()
});
var ErrorResponseSchema = z2.object({
  message: z2.string(),
  code: z2.string().optional(),
  details: z2.record(z2.unknown()).optional()
});
var SuccessResponseSchema = (dataSchema) => z2.object({
  success: z2.literal(true),
  data: dataSchema
});

// src/contracts/approvals.contract.ts
var c = initContract();
var approvalsContract = c.router({
  /**
   * List pending approvals awaiting decision
   */
  listPending: {
    method: "GET",
    path: "/approvals/pending",
    query: ApprovalsQuerySchema,
    responses: {
      200: PaginatedResponseSchema(PendingSignatureSchema)
    },
    summary: "List pending approvals",
    description: "Returns paginated list of signing workflows awaiting human approval"
  },
  /**
   * Get approval details by pending signature ID
   */
  getById: {
    method: "GET",
    path: "/approvals/:id",
    pathParams: z3.object({
      id: z3.string()
    }),
    responses: {
      200: z3.object({
        pending: PendingSignatureSchema,
        signatures: z3.array(
          z3.object({
            department: z3.string(),
            signerAddress: z3.string(),
            signedAt: z3.string().datetime()
          })
        ),
        approvals: z3.array(
          z3.object({
            approverId: z3.string(),
            decision: z3.enum(["APPROVED", "REJECTED"]),
            reason: z3.string().nullable(),
            decidedAt: z3.string().datetime()
          })
        )
      }),
      404: ErrorResponseSchema
    },
    summary: "Get approval details",
    description: "Returns full details of a signing workflow including signatures and approval history"
  },
  /**
   * Submit an approval decision
   */
  decide: {
    method: "POST",
    path: "/approvals/:id/decide",
    pathParams: z3.object({
      id: z3.string()
    }),
    body: SubmitApprovalSchema,
    responses: {
      200: SigningWorkflowResultSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      409: ErrorResponseSchema
    },
    summary: "Submit approval decision",
    description: "Submit an approval or rejection decision for a signing workflow"
  },
  /**
   * Get signature details by event ID
   */
  getSignaturesByEventId: {
    method: "GET",
    path: "/signatures/event/:eventId",
    pathParams: z3.object({
      eventId: z3.string()
    }),
    responses: {
      200: SigningWorkflowResultSchema,
      404: ErrorResponseSchema
    },
    summary: "Get signatures by event ID",
    description: "Look up signing workflow status by the event ID"
  },
  /**
   * Get signature details by pending signature ID
   */
  getSignaturesById: {
    method: "GET",
    path: "/signatures/:id",
    pathParams: z3.object({
      id: z3.string()
    }),
    responses: {
      200: SigningWorkflowResultSchema,
      404: ErrorResponseSchema
    },
    summary: "Get signatures by ID",
    description: "Look up signing workflow status by pending signature ID"
  },
  /**
   * List all signing workflows
   */
  listSignatures: {
    method: "GET",
    path: "/signatures",
    query: SignaturesQuerySchema,
    responses: {
      200: PaginatedResponseSchema(PendingSignatureSchema)
    },
    summary: "List signing workflows",
    description: "Returns paginated list of all signing workflows with filters"
  },
  /**
   * Collect a department signature (internal/admin use)
   */
  collectSignature: {
    method: "POST",
    path: "/signatures/:id/collect",
    pathParams: z3.object({
      id: z3.string()
    }),
    body: z3.object({
      department: z3.enum(["origination", "risk", "compliance", "operations"])
    }).strict(),
    responses: {
      200: SigningWorkflowResultSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      409: ErrorResponseSchema
    },
    summary: "Collect department signature",
    description: "Trigger signature collection from a specific department (admin/internal use)"
  }
});

// src/contracts/events.contract.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z8 } from "zod";

// src/schemas/events.ts
import { z as z7 } from "zod";

// src/schemas/actor.ts
import { z as z4 } from "zod";
var ActorTypeSchema = z4.enum(["USER", "SYSTEM", "INTEGRATION"]);
var ActorSchema = z4.object({
  id: z4.string().min(1).max(100),
  type: ActorTypeSchema,
  department: z4.string().min(1).max(50).optional()
}).strict();
var ACTOR_TYPE_DESCRIPTIONS = {
  USER: "Human user performing action through the system",
  SYSTEM: "Automated system process or background job",
  INTEGRATION: "External system or third-party integration"
};
function getActorTypeDescription(type) {
  return ACTOR_TYPE_DESCRIPTIONS[type];
}
var COMMON_DEPARTMENTS = [
  "origination",
  "processing",
  "underwriting",
  "closing",
  "funding",
  "quality_assurance",
  "compliance",
  "servicing",
  "customer_service",
  "automation",
  "crm",
  "los"
  // Loan Origination System
];
function createSystemActor(systemId = "SYSTEM", department) {
  return {
    id: systemId,
    type: "SYSTEM",
    department
  };
}
function createUserActor(userId, department) {
  return {
    id: userId,
    type: "USER",
    department
  };
}
function createIntegrationActor(integrationId, department) {
  return {
    id: integrationId,
    type: "INTEGRATION",
    department
  };
}

// src/schemas/loan-case.ts
import { z as z5 } from "zod";
var ApplicationStageSchema = z5.enum([
  "PRE_APPROVAL",
  "LOAN_APPLICATION"
]);
var LoanCaseStatusSchema = z5.enum([
  "DRAFT",
  "SUBMITTED",
  "REJECTED",
  "CONVERTED",
  "PENDING",
  "ACTIVE",
  "PAID_OFF",
  "DEFAULTED",
  "FORECLOSED",
  "CANCELLED"
]);
var LoanCaseEventCategorySchema = z5.enum([
  "APPLICATION_LIFECYCLE",
  "AUDIT_LOG",
  "API_REQUEST",
  "BLOCKCHAIN",
  "COMPLIANCE",
  "DOCUMENT"
]);
var LoanCaseRefSchema = z5.discriminatedUnion("kind", [
  z5.object({ kind: z5.literal("by-id"), loanCaseId: CuidSchema }).strict(),
  z5.object({
    kind: z5.literal("by-external"),
    externalLoanId: z5.string().min(1).max(100),
    applicationStage: ApplicationStageSchema
  }).strict()
]);
var LoanCaseSchema = z5.object({
  id: CuidSchema,
  externalLoanId: z5.string().nullable(),
  applicationStage: ApplicationStageSchema,
  loanCaseNumber: z5.string().nullable(),
  creatorActorId: z5.string(),
  primaryBankerActorId: z5.string().nullable(),
  secondaryBankerActorId: z5.string().nullable(),
  underwriterActorId: z5.string().nullable(),
  processorActorId: z5.string().nullable(),
  parentLoanCaseId: z5.string().nullable(),
  status: LoanCaseStatusSchema,
  borrowerAddress: z5.string().nullable(),
  lenderAddress: z5.string().nullable(),
  propertyAddress: z5.string().nullable(),
  propertyCity: z5.string().nullable(),
  propertyState: z5.string().nullable(),
  propertyZip: z5.string().nullable(),
  appraisedValue: DecimalStringSchema.nullable(),
  principal: DecimalStringSchema.nullable(),
  interestRate: z5.number().int().nullable(),
  termMonths: z5.number().int().nullable(),
  monthlyPayment: DecimalStringSchema.nullable(),
  startDate: z5.string().datetime().nullable(),
  maturityDate: z5.string().datetime().nullable(),
  merkleRoot: z5.string().nullable(),
  txHash: z5.string().nullable(),
  blockNumber: z5.string().nullable(),
  // BigInt serialized as string
  createdAt: z5.string().datetime(),
  updatedAt: z5.string().datetime()
});
var CreateLoanCaseSchema = z5.object({
  externalLoanId: z5.string().min(1).max(100).optional(),
  applicationStage: ApplicationStageSchema,
  loanCaseNumber: z5.string().optional(),
  creatorActorId: z5.string().min(1),
  primaryBankerActorId: z5.string().min(1).optional(),
  secondaryBankerActorId: z5.string().min(1).optional(),
  underwriterActorId: z5.string().min(1).optional(),
  processorActorId: z5.string().min(1).optional(),
  parentLoanCaseId: CuidSchema.optional(),
  borrowerAddress: z5.string().optional(),
  lenderAddress: z5.string().optional(),
  propertyAddress: z5.string().optional(),
  propertyCity: z5.string().optional(),
  propertyState: z5.string().length(2).regex(/^[A-Z]{2}$/).optional(),
  propertyZip: z5.string().regex(/^\d{5}(-\d{4})?$/).optional(),
  appraisedValue: DecimalStringSchema.optional(),
  principal: DecimalStringSchema.optional(),
  interestRate: z5.number().int().min(0).max(1e4).optional(),
  termMonths: z5.number().int().min(1).max(600).optional(),
  monthlyPayment: DecimalStringSchema.optional()
}).strict();
var UpdateLoanCaseStatusSchema = z5.object({
  status: LoanCaseStatusSchema
}).strict();
var SetUserOfRecordSchema = z5.object({
  primaryBankerActorId: z5.string().min(1)
}).strict();
var LoanCaseEventSchema = z5.object({
  id: CuidSchema,
  loanCaseId: CuidSchema,
  category: LoanCaseEventCategorySchema,
  eventType: z5.string(),
  data: z5.record(z5.unknown()),
  note: z5.string().nullable(),
  hash: z5.string().nullable(),
  txHash: z5.string().nullable(),
  blockNumber: z5.string().nullable(),
  respaServiceCode: z5.string().nullable(),
  respaServiceName: z5.string().nullable(),
  actorId: z5.string(),
  actorType: ActorSchema.shape.type,
  actorDepartment: z5.string().nullable(),
  ipAddress: z5.string().nullable(),
  userAgent: z5.string().nullable(),
  besuTxHash: z5.string().nullable(),
  besuBlockNumber: z5.string().nullable(),
  besuTimestamp: z5.string().datetime().nullable(),
  merkleBatchId: z5.string().nullable(),
  leafIndex: z5.number().int().nullable(),
  createdAt: z5.string().datetime()
});
var LoanCaseWithEventsSchema = LoanCaseSchema.extend({
  events: z5.array(LoanCaseEventSchema)
});
var LoanCaseQuerySchema = z5.object({
  externalLoanId: z5.string().optional(),
  applicationStage: ApplicationStageSchema.optional(),
  primaryBankerActorId: z5.string().optional(),
  status: LoanCaseStatusSchema.optional(),
  limit: z5.coerce.number().int().min(1).max(100).default(20),
  offset: z5.coerce.number().int().min(0).default(0)
}).strict();
var LoanCaseListResponseSchema = z5.object({
  items: z5.array(LoanCaseSchema),
  total: z5.number().int(),
  limit: z5.number().int(),
  offset: z5.number().int()
});
var LoanCaseAuditSchema = z5.object({
  loanCase: LoanCaseSchema,
  events: z5.array(LoanCaseEventSchema),
  totalEvents: z5.number().int(),
  anchoredEvents: z5.number().int(),
  pendingEvents: z5.number().int()
});
var RecordPaymentSchema = z5.object({
  amount: DecimalStringSchema,
  principal: DecimalStringSchema,
  interest: DecimalStringSchema,
  escrow: DecimalStringSchema,
  paymentDate: z5.string().datetime()
}).strict();
var PaymentSchema = z5.object({
  id: CuidSchema,
  loanCaseId: CuidSchema,
  amount: DecimalStringSchema,
  principal: DecimalStringSchema,
  interest: DecimalStringSchema,
  escrow: DecimalStringSchema,
  paymentDate: z5.string().datetime(),
  txHash: z5.string().nullable(),
  createdAt: z5.string().datetime()
});
var PaymentsListResponseSchema = z5.object({
  items: z5.array(PaymentSchema),
  total: z5.number().int()
});

// src/schemas/respa.ts
import { z as z6 } from "zod";
var RESPAServiceCodeSchema = z6.enum([
  "a",
  // Origination Services
  "b",
  // Services Borrower Did Not/Could Not Shop For
  "c",
  // Services Borrower Could Shop For
  "d",
  // Total Loan Costs
  "e",
  // Taxes and Other Government Fees
  "f",
  // Prepaids
  "g",
  // Initial Escrow Payment at Closing
  "h",
  // Other Costs
  "i",
  // Total Other Costs
  "j",
  // Total Closing Costs
  "k",
  // Lender Credits
  "l",
  // Cash to Close
  "m"
  // Other Settlement Services
]);
var RESPA_SERVICE_NAMES = {
  a: "Origination Services",
  b: "Services Borrower Did Not/Could Not Shop For",
  c: "Services Borrower Could Shop For",
  d: "Total Loan Costs",
  e: "Taxes and Other Government Fees",
  f: "Prepaids",
  g: "Initial Escrow Payment at Closing",
  h: "Other Costs",
  i: "Total Other Costs",
  j: "Total Closing Costs",
  k: "Lender Credits",
  l: "Cash to Close",
  m: "Other Settlement Services"
};
var EVENT_TO_RESPA_CODE = {
  // Origination Services (a)
  APPLICATION_SUBMITTED: "a",
  APPLICATION_REVIEWED: "a",
  CREDIT_REPORT_ORDERED: "a",
  UNDERWRITING_INITIATED: "a",
  UNDERWRITING_COMPLETED: "a",
  LOAN_APPROVED: "a",
  LOAN_DENIED: "a",
  // Services Borrower Did Not/Could Not Shop For (b)
  APPRAISAL_ORDERED: "b",
  APPRAISAL_COMPLETED: "b",
  ORDER_APPRAISAL_API_PULLED: "b",
  TITLE_SEARCH_INITIATED: "b",
  TITLE_SEARCH_COMPLETED: "b",
  FLOOD_CERTIFICATION_ORDERED: "b",
  TAX_SERVICE_INITIATED: "b",
  // Services Borrower Could Shop For (c)
  SURVEY_ORDERED: "c",
  PEST_INSPECTION_ORDERED: "c",
  HOME_INSPECTION_SCHEDULED: "c",
  SETTLEMENT_AGENT_SELECTED: "c",
  // Taxes and Other Government Fees (e)
  RECORDING_FEE_CALCULATED: "e",
  TRANSFER_TAX_CALCULATED: "e",
  // Prepaids (f)
  INSURANCE_PREMIUM_CALCULATED: "f",
  PROPERTY_TAX_PREPAID: "f",
  HOMEOWNERS_INSURANCE_RECEIVED: "f",
  // Initial Escrow Payment at Closing (g)
  ESCROW_ACCOUNT_CREATED: "g",
  ESCROW_PAYMENT_CALCULATED: "g",
  // Other Settlement Services (m)
  DOCUMENT_RECEIVED: "m",
  DOCUMENT_VERIFIED: "m",
  DOCUMENT_REJECTED: "m",
  VOE_INITIATED: "m",
  VOE_COMPLETED: "m",
  VOD_INITIATED: "m",
  VOD_COMPLETED: "m",
  INCOME_VERIFICATION_INITIATED: "m",
  INCOME_VERIFICATION_COMPLETED: "m",
  ASSET_VERIFICATION_INITIATED: "m",
  ASSET_VERIFICATION_COMPLETED: "m",
  CLOSING_SCHEDULED: "m",
  CLOSING_COMPLETED: "m",
  FUNDING_INITIATED: "m",
  FUNDING_COMPLETED: "m",
  // LoanCase lifecycle events (default to Other Settlement Services)
  LOAN_CASE_CREATED: "m",
  LOAN_CASE_ACTIVATED: "m",
  LOAN_CASE_VERIFICATION_REQUESTED: "m",
  LOAN_CASE_VERIFICATION_COMPLETED: "m",
  PAYMENT_RECEIVED: "m",
  STATUS_CHANGED: "m",
  DOCUMENT_UPLOADED: "m",
  DOCUMENT_DOWNLOADED: "m",
  DOCUMENT_DELETED: "m",
  ORDER_PMI_API_PULLED: "m",
  RUNCAP_API_PULLED: "m",
  ORDER_CREDIT_API_PULLED: "m",
  ORDER_FLOOD_CERT_API_PULLED: "m",
  ORDER_CREDIT_UPDATED: "m",
  NOTE_ADDED: "m",
  ORDER_TITLE_API_PULLED: "m",
  ANCHORED_ON_CHAIN: "m"
};
function getRESPAServiceCode(eventType) {
  return EVENT_TO_RESPA_CODE[eventType] || "m";
}
function getRESPAServiceName(code) {
  return RESPA_SERVICE_NAMES[code];
}
function categorizeEventByRESPA(eventType) {
  const code = getRESPAServiceCode(eventType);
  return {
    code,
    name: getRESPAServiceName(code)
  };
}
var EVENT_TYPES = Object.keys(EVENT_TO_RESPA_CODE);
function getEventTypesForRESPACode(code) {
  return Object.entries(EVENT_TO_RESPA_CODE).filter(([, respaCode]) => respaCode === code).map(([eventType]) => eventType);
}

// src/schemas/events.ts
var EventTypeSchema = z7.enum([
  // === LoanCase Lifecycle ===
  "LOAN_CASE_CREATED",
  "LOAN_CASE_ACTIVATED",
  "LOAN_CASE_VERIFICATION_REQUESTED",
  "LOAN_CASE_VERIFICATION_COMPLETED",
  // === Legacy / Existing Event Types ===
  "PAYMENT_RECEIVED",
  "STATUS_CHANGED",
  "ANCHORED_ON_CHAIN",
  // === Document Actions ===
  "DOCUMENT_UPLOADED",
  "DOCUMENT_DOWNLOADED",
  "DOCUMENT_DELETED",
  // === Application Phase ===
  "APPLICATION_SUBMITTED",
  "APPLICATION_UPDATED",
  "RUNCAP_API_PULLED",
  "PREQUALIFICATION_COMPLETED",
  // === Documentation Phase ===
  "DOCUMENT_REQUESTED",
  "DOCUMENT_RECEIVED",
  "DOCUMENT_VERIFIED",
  "DOCUMENT_REJECTED",
  // === Verification Phase ===
  "VOE_INITIATED",
  // Verification of Employment
  "VOE_COMPLETED",
  "VOD_INITIATED",
  // Verification of Deposit
  "VOD_COMPLETED",
  "ORDER_CREDIT_API_PULLED",
  // === Appraisal & Inspection Phase ===
  "INSPECTION_ORDERED",
  "INSPECTION_COMPLETED",
  "ORDER_APPRAISAL_API_PULLED",
  "ORDER_FLOOD_CERT_API_PULLED",
  "ORDER_PMI_API_PULLED",
  // === Disclosure Phase ===
  "DISCLOSURE_SENT",
  "DISCLOSURE_ACKNOWLEDGED",
  "GFE_PROVIDED",
  // Good Faith Estimate
  "TILA_PROVIDED",
  // Truth in Lending Act
  // === Compliance Phase ===
  "BORROWER_COUNSELED",
  "ORDER_CREDIT_UPDATED",
  "NOTE_ADDED",
  // === Closing Phase ===
  "ORDER_TITLE_API_PULLED",
  "CLOSING_SCHEDULED",
  "CLOSING_DOCUMENTS_ORDERED",
  "LOAN_CLOSED",
  "LOAN_FUNDED"
]);
var EventMetadataSchema = z7.object({
  correlationId: z7.string().uuid().optional().describe("UUID for correlating events across systems"),
  source: z7.string().min(1).max(100).optional().describe("Source system or service"),
  version: z7.string().regex(/^\d+\.\d+\.\d+$/).default("1.0.0").describe("Event schema version"),
  ipAddress: z7.string().ip().optional().describe("IP address of the originating client"),
  userAgent: z7.string().max(500).optional().describe("User agent string from the client")
}).strict();
var CreateEventSchema = z7.object({
  loanCase: LoanCaseRefSchema.describe(
    "Reference to the LoanCase this event belongs to"
  ),
  eventType: EventTypeSchema.describe("Type of event"),
  data: z7.record(z7.unknown()).describe("Event-specific data payload"),
  actor: ActorSchema.describe(
    "Actor who performed the action (user, system, or integration)"
  ),
  category: LoanCaseEventCategorySchema.optional().describe(
    "Event category; defaults to APPLICATION_LIFECYCLE when omitted"
  ),
  note: z7.string().max(1e3).optional().describe("Optional free-text note"),
  respaServiceCode: RESPAServiceCodeSchema.optional().describe(
    "RESPA service code for compliance tracking"
  ),
  metadata: EventMetadataSchema.optional().describe(
    "Additional metadata for tracing and versioning"
  )
}).strict();
var EventSchema = z7.object({
  id: CuidSchema,
  loanCaseId: CuidSchema,
  eventType: z7.string(),
  data: z7.record(z7.unknown()),
  hash: z7.string().nullable().describe("Keccak-256 hash of canonicalized event"),
  txHash: z7.string().nullable().describe("Transaction hash if anchored on-chain"),
  blockNumber: z7.string().nullable().describe("Block number if anchored"),
  createdAt: z7.string().datetime()
});
var EnhancedEventSchema = EventSchema.extend({
  actor: ActorSchema.nullable().describe("Actor who performed the action"),
  respaServiceCode: z7.string().max(2).nullable().describe("RESPA service code (a-m)"),
  respaServiceName: z7.string().max(100).nullable().describe("RESPA service name"),
  metadata: EventMetadataSchema.nullable().describe("Event metadata"),
  besuTxHash: z7.string().length(66).nullable().describe("Besu transaction hash (0x-prefixed)"),
  besuBlockNumber: z7.string().nullable().describe("Besu block number as string"),
  besuTimestamp: z7.string().datetime().nullable().describe("Timestamp of Besu confirmation"),
  merkleBatchId: z7.string().nullable().describe("ID of the merkle batch containing this event"),
  leafIndex: z7.number().int().min(0).nullable().describe("Index of this event's leaf in the merkle tree")
});
var EventWithProofSchema = EventSchema.extend({
  proof: z7.object({
    merkleRoot: z7.string(),
    merkleProof: z7.array(z7.string()),
    leafIndex: z7.number(),
    verified: z7.boolean()
  }).nullable()
});
var EventQuerySchema = z7.object({
  loanCaseId: CuidSchema.optional(),
  eventType: EventTypeSchema.optional(),
  fromDate: z7.string().datetime().optional(),
  toDate: z7.string().datetime().optional(),
  page: z7.coerce.number().int().min(1).default(1),
  limit: z7.coerce.number().int().min(1).max(100).default(50)
});
var BatchEventsSchema = z7.object({
  eventIds: z7.array(CuidSchema).min(1).max(1e3).describe("Event IDs to batch")
}).strict();
var MerkleBatchSchema = z7.object({
  id: CuidSchema,
  root: z7.string().describe("Merkle root hash"),
  leaves: z7.array(z7.string()).describe("Leaf hashes in order"),
  chainId: z7.number().describe("Target chain ID for anchoring"),
  txHash: z7.string().nullable(),
  blockNumber: z7.string().nullable(),
  anchoredAt: z7.string().datetime().nullable(),
  createdAt: z7.string().datetime()
});

// src/contracts/events.contract.ts
var c2 = initContract2();
var eventsContract = c2.router({
  /**
   * Create a new event
   */
  create: {
    method: "POST",
    path: "/events",
    body: CreateEventSchema,
    responses: {
      201: EventSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema
    },
    summary: "Create event",
    description: "Creates a new LoanCase event with automatic hash computation"
  },
  /**
   * List events with filters
   */
  list: {
    method: "GET",
    path: "/events",
    query: EventQuerySchema,
    responses: {
      200: PaginatedResponseSchema(EventSchema)
    },
    summary: "List events",
    description: "Returns a paginated list of events with optional filters"
  },
  /**
   * Get event by ID
   */
  getById: {
    method: "GET",
    path: "/events/:id",
    pathParams: z8.object({
      id: z8.string()
    }),
    responses: {
      200: EventWithProofSchema,
      404: ErrorResponseSchema
    },
    summary: "Get event by ID",
    description: "Returns an event with its merkle proof if anchored"
  },
  /**
   * Get event by hash
   */
  getByHash: {
    method: "GET",
    path: "/events/hash/:hash",
    pathParams: z8.object({
      hash: z8.string()
    }),
    responses: {
      200: EventWithProofSchema,
      404: ErrorResponseSchema
    },
    summary: "Get event by hash",
    description: "Look up an event by its hash"
  },
  /**
   * Batch events into a merkle tree
   */
  batchEvents: {
    method: "POST",
    path: "/events/batch",
    body: BatchEventsSchema,
    responses: {
      201: MerkleBatchSchema,
      400: ErrorResponseSchema
    },
    summary: "Batch events",
    description: "Creates a merkle tree from specified events"
  },
  /**
   * List merkle batches
   */
  listBatches: {
    method: "GET",
    path: "/merkle/batches",
    query: z8.object({
      chainId: z8.coerce.number().optional(),
      anchored: z8.enum(["true", "false"]).optional(),
      page: z8.coerce.number().int().min(1).default(1),
      limit: z8.coerce.number().int().min(1).max(100).default(20)
    }),
    responses: {
      200: PaginatedResponseSchema(MerkleBatchSchema)
    },
    summary: "List merkle batches",
    description: "Returns paginated list of merkle batches"
  },
  /**
   * Get merkle batch by root
   */
  getBatchByRoot: {
    method: "GET",
    path: "/merkle/batches/:root",
    pathParams: z8.object({
      root: z8.string()
    }),
    responses: {
      200: MerkleBatchSchema,
      404: ErrorResponseSchema
    },
    summary: "Get batch by root",
    description: "Look up a merkle batch by its root hash"
  },
  /**
   * Anchor a batch on-chain
   */
  anchorBatch: {
    method: "POST",
    path: "/merkle/batches/:id/anchor",
    pathParams: z8.object({
      id: z8.string()
    }),
    body: z8.object({
      chainId: z8.number().describe("Target chain ID (31337=Anvil, 1337=Besu)")
    }).strict(),
    responses: {
      200: MerkleBatchSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema
    },
    summary: "Anchor batch on-chain",
    description: "Submits the merkle root to the specified blockchain"
  }
});

// src/contracts/health.contract.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { z as z9 } from "zod";
var c3 = initContract3();
var HealthResponseSchema = z9.object({
  status: z9.enum(["ok", "degraded", "unhealthy"]),
  timestamp: z9.string().datetime(),
  version: z9.string().optional(),
  services: z9.object({
    database: z9.enum(["connected", "disconnected"]),
    anvil: z9.enum(["connected", "disconnected"]).optional(),
    besu: z9.enum(["connected", "disconnected"]).optional(),
    redis: z9.enum(["connected", "disconnected"]).optional()
  }).optional()
});
var healthContract = c3.router({
  /**
   * Basic health check
   */
  check: {
    method: "GET",
    path: "/health",
    responses: {
      200: HealthResponseSchema
    },
    summary: "Health check",
    description: "Returns the health status of the API and its dependencies"
  },
  /**
   * Detailed health check with service status
   */
  detailed: {
    method: "GET",
    path: "/health/detailed",
    responses: {
      200: HealthResponseSchema,
      503: z9.object({
        status: z9.literal("unhealthy"),
        error: z9.string(),
        services: z9.record(z9.string())
      })
    },
    summary: "Detailed health check",
    description: "Returns detailed health status including all dependent services"
  },
  /**
   * Readiness probe (for Kubernetes)
   */
  ready: {
    method: "GET",
    path: "/health/ready",
    responses: {
      200: z9.object({ ready: z9.literal(true) }),
      503: z9.object({ ready: z9.literal(false), reason: z9.string() })
    },
    summary: "Readiness probe",
    description: "Indicates if the service is ready to accept traffic"
  },
  /**
   * Liveness probe (for Kubernetes)
   */
  live: {
    method: "GET",
    path: "/health/live",
    responses: {
      200: z9.object({ alive: z9.literal(true) })
    },
    summary: "Liveness probe",
    description: "Indicates if the service is alive"
  }
});

// src/contracts/loan-case.contract.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z10 } from "zod";
var c4 = initContract4();
var loanCaseContract = c4.router({
  /**
   * Create a new LoanCase.
   * Supports sparse pre-approval records (most financial fields optional).
   */
  create: {
    method: "POST",
    path: "/loan-cases",
    body: CreateLoanCaseSchema,
    responses: {
      201: LoanCaseSchema,
      400: ErrorResponseSchema,
      422: ErrorResponseSchema
    },
    summary: "Create LoanCase",
    description: "Creates a new LoanCase record. applicationStage is required; financial and property fields are optional to support sparse pre-approval records."
  },
  /**
   * List LoanCases with optional filters.
   */
  list: {
    method: "GET",
    path: "/loan-cases",
    query: LoanCaseQuerySchema,
    responses: {
      200: LoanCaseListResponseSchema
    },
    summary: "List LoanCases",
    description: "Returns a paginated list of LoanCases. Supports filtering by status, applicationStage, externalLoanId, and primaryBankerActorId."
  },
  /**
   * Get a single LoanCase with its embedded events.
   */
  getById: {
    method: "GET",
    path: "/loan-cases/:id",
    pathParams: z10.object({
      id: z10.string()
    }),
    responses: {
      200: LoanCaseWithEventsSchema,
      404: ErrorResponseSchema
    },
    summary: "Get LoanCase by ID",
    description: "Returns a LoanCase with all associated events."
  },
  /**
   * Update status with state-machine validation enforced server-side.
   */
  updateStatus: {
    method: "PATCH",
    path: "/loan-cases/:id/status",
    pathParams: z10.object({
      id: z10.string()
    }),
    body: UpdateLoanCaseStatusSchema,
    responses: {
      200: LoanCaseSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      422: ErrorResponseSchema
    },
    summary: "Update LoanCase status",
    description: "Transitions a LoanCase to a new status. Valid transitions are enforced by the server-side state machine."
  },
  /**
   * Set the primary banker (user-of-record) for a LoanCase.
   */
  setUserOfRecord: {
    method: "PATCH",
    path: "/loan-cases/:id/user-of-record",
    pathParams: z10.object({
      id: z10.string()
    }),
    body: SetUserOfRecordSchema,
    responses: {
      200: LoanCaseSchema,
      404: ErrorResponseSchema
    },
    summary: "Set user of record",
    description: "Assigns a primaryBankerActorId to the LoanCase. This person becomes the user-of-record for compliance audit purposes."
  },
  /**
   * Full audit trail for a LoanCase including all events and anchoring counts.
   */
  getAudit: {
    method: "GET",
    path: "/loan-cases/:id/audit",
    pathParams: z10.object({
      id: z10.string()
    }),
    responses: {
      200: LoanCaseAuditSchema,
      404: ErrorResponseSchema
    },
    summary: "Get LoanCase audit trail",
    description: "Returns the LoanCase with its complete event history plus anchoring summary counts."
  },
  /**
   * Record a payment against a LoanCase.
   */
  recordPayment: {
    method: "POST",
    path: "/loan-cases/:id/payments",
    pathParams: z10.object({
      id: z10.string()
    }),
    body: RecordPaymentSchema,
    responses: {
      201: PaymentSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema
    },
    summary: "Record payment",
    description: "Records a payment against a LoanCase."
  },
  /**
   * List all payments for a LoanCase.
   */
  listPayments: {
    method: "GET",
    path: "/loan-cases/:id/payments",
    pathParams: z10.object({
      id: z10.string()
    }),
    responses: {
      200: PaymentsListResponseSchema,
      404: ErrorResponseSchema
    },
    summary: "List payments",
    description: "Returns all payments recorded against a LoanCase."
  }
});

// src/contracts/public-verification.contract.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z13 } from "zod";

// src/schemas/public-verification.ts
import { z as z12 } from "zod";

// src/schemas/verification.ts
import { z as z11 } from "zod";
var VerifyEventSchema = z11.object({
  eventId: CuidSchema.describe("ID of the event to verify")
}).strict();
var VerifyByHashSchema = z11.object({
  hash: z11.string().regex(/^0x[0-9a-f]{64}$/, "Invalid hash format")
}).strict();
var VerificationResultSchema = z11.object({
  verified: z11.boolean().describe("Whether the verification passed"),
  // Event details
  eventId: CuidSchema.optional(),
  eventHash: z11.string(),
  eventType: z11.string().optional(),
  // Merkle proof details
  merkleRoot: z11.string().optional(),
  merkleProof: z11.array(z11.string()).optional(),
  leafIndex: z11.number().optional(),
  // On-chain details
  chainId: z11.number().optional(),
  chainName: z11.string().optional(),
  txHash: z11.string().nullable().optional(),
  blockNumber: z11.string().nullable().optional(),
  blockTimestamp: z11.string().datetime().nullable().optional(),
  // Verification metadata
  verifiedAt: z11.string().datetime(),
  method: z11.enum(["merkle_proof", "direct_lookup", "on_chain"]),
  // Error details (if not verified)
  error: z11.string().optional()
});
var BatchVerifySchema = z11.object({
  hashes: z11.array(z11.string().regex(/^0x[0-9a-f]{64}$/)).min(1).max(100)
}).strict();
var BatchVerificationResultSchema = z11.object({
  results: z11.array(VerificationResultSchema),
  summary: z11.object({
    total: z11.number(),
    verified: z11.number(),
    failed: z11.number()
  })
});
var ChainStatusSchema = z11.object({
  chainId: z11.number(),
  chainName: z11.string(),
  rpcUrl: z11.string(),
  connected: z11.boolean(),
  latestBlock: z11.string().nullable(),
  lastAnchorTx: z11.string().nullable(),
  lastAnchorBlock: z11.string().nullable(),
  lastAnchorTime: z11.string().datetime().nullable(),
  pendingAnchors: z11.number()
});
var AuditTrailSchema = z11.object({
  loanCaseId: CuidSchema,
  events: z11.array(
    z11.object({
      eventId: CuidSchema,
      eventType: z11.string(),
      hash: z11.string(),
      verified: z11.boolean(),
      anchoredAt: z11.string().datetime().nullable(),
      chainId: z11.number().nullable(),
      txHash: z11.string().nullable()
    })
  ),
  summary: z11.object({
    totalEvents: z11.number(),
    anchoredEvents: z11.number(),
    verifiedEvents: z11.number(),
    pendingEvents: z11.number()
  }),
  generatedAt: z11.string().datetime()
});
var HashAlgorithmSchema = z11.enum([
  "RFC6962_SHA256",
  "KECCAK256_MERKLETREEJS"
]);
var PublicAnchorStatusSchema = z11.enum([
  "PENDING",
  "SUBMITTING",
  "SUBMITTED",
  "CONFIRMED",
  "FAILED",
  "DEAD_LETTER"
]);
var IntegrityAlertTypeSchema = z11.enum([
  "ROOT_MISMATCH",
  "ORPHAN_ANCHOR",
  "DUPLICATE_ROOT",
  "OUT_OF_ORDER",
  "MISSING_ANCHOR",
  "CHAIN_UNREACHABLE",
  "NON_USER_OF_RECORD"
]);
var AlertSeveritySchema = z11.enum([
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL"
]);
var KnownChainIdSchema = z11.union([
  z11.literal(1),
  z11.literal(137),
  z11.literal(1337),
  z11.literal(31337),
  z11.literal(80002)
]);
var Hex64Schema = z11.string().regex(
  /^0x[0-9a-f]{64}$/,
  "Must be a 0x-prefixed 64-character lowercase hex string"
);
var EventProofParamsSchema = z11.object({
  eventId: CuidSchema.describe("CUID of the LoanCase event")
}).strict();
var AnchorSummarySchema = z11.object({
  chainId: z11.number().int().positive(),
  txHash: z11.string(),
  blockNumber: z11.string().nullable(),
  confirmedAt: z11.string().datetime().nullable(),
  status: PublicAnchorStatusSchema
});
var EventInclusionProofSchema = z11.object({
  eventId: z11.string(),
  eventHash: Hex64Schema,
  merkleBatch: z11.object({
    id: z11.string(),
    merkleRoot: Hex64Schema,
    hashAlgorithm: HashAlgorithmSchema,
    recordCount: z11.number().int().min(0),
    batchDate: z11.string().datetime()
  }),
  proof: z11.object({
    leafIndex: z11.number().int().min(0),
    treeSize: z11.number().int().min(1),
    siblings: z11.array(Hex64Schema)
  }),
  anchors: z11.array(AnchorSummarySchema)
});
var ConsistencyProofParamsSchema = z11.object({
  batchId: CuidSchema.describe("CUID of the newer batch"),
  olderBatchId: CuidSchema.describe("CUID of the older batch")
}).strict();
var BatchSummarySchema = z11.object({
  id: z11.string(),
  root: Hex64Schema,
  recordCount: z11.number().int().min(0)
});
var ConsistencyProofResponseSchema = z11.object({
  newerBatch: BatchSummarySchema,
  olderBatch: BatchSummarySchema,
  hashAlgorithm: HashAlgorithmSchema,
  consistencyProof: z11.array(Hex64Schema)
});
var IntegrityAlertsQuerySchema = z11.object({
  resolved: z11.enum(["true", "false"]).transform((v) => v === "true").optional().describe("Filter by resolved status"),
  severity: AlertSeveritySchema.optional().describe(
    "Filter by alert severity"
  ),
  limit: z11.coerce.number().int().min(1).max(250).default(50).describe("Maximum results to return (default 50, max 250)"),
  offset: z11.coerce.number().int().min(0).default(0).describe("Number of results to skip")
}).strict();
var IntegrityAlertItemSchema = z11.object({
  id: z11.string(),
  alertType: IntegrityAlertTypeSchema,
  severity: AlertSeveritySchema,
  batchId: z11.string().nullable(),
  anchorId: z11.string().nullable(),
  chainId: z11.number().int().nullable(),
  expectedRoot: z11.string().nullable(),
  observedRoot: z11.string().nullable(),
  details: z11.record(z11.unknown()),
  resolved: z11.boolean(),
  resolvedBy: z11.string().nullable(),
  resolvedAt: z11.string().datetime().nullable(),
  createdAt: z11.string().datetime()
});
var IntegrityAlertsResponseSchema = z11.object({
  items: z11.array(IntegrityAlertItemSchema),
  pagination: z11.object({
    total: z11.number().int().min(0),
    limit: z11.number().int().min(1),
    offset: z11.number().int().min(0)
  })
});
var AnchorLookupParamsSchema = z11.object({
  chainId: z11.coerce.number().pipe(KnownChainIdSchema).describe("Chain ID (1, 137, 1337, 31337, or 80002)"),
  txHash: TxHashSchema.describe("0x-prefixed 64-hex-char transaction hash")
}).strict();
var AnchorLookupResponseSchema = z11.object({
  anchor: z11.object({
    id: z11.string(),
    batchId: z11.string(),
    chainId: z11.number().int().positive(),
    txHash: z11.string(),
    blockNumber: z11.string().nullable(),
    status: PublicAnchorStatusSchema,
    confirmedAt: z11.string().datetime().nullable(),
    submittedAt: z11.string().datetime()
  }),
  batch: z11.object({
    id: z11.string(),
    merkleRoot: Hex64Schema,
    recordCount: z11.number().int().min(0),
    batchDate: z11.string().datetime()
  })
});

// src/schemas/public-verification.ts
var PublicEventSchema = z12.object({
  hash: TxHashSchema.describe("Cryptographic hash of the event"),
  eventType: z12.string().describe("Event type enum string"),
  respaCode: RESPAServiceCodeSchema.describe("RESPA Section 8 service code"),
  respaName: z12.string().describe("Human-readable RESPA service name"),
  timestamp: z12.string().datetime().describe("ISO timestamp when the event was recorded"),
  loanIdHash: z12.string().describe(
    "Stable opaque hash of the loan ID \u2014 derived server-side; raw loanId never exposed"
  ),
  verified: z12.boolean().describe("Whether the event passed merkle proof verification"),
  anchored: z12.boolean().describe("Whether the event batch has been anchored on-chain"),
  merkleRoot: z12.string().nullable().describe("Merkle root of the batch containing this event"),
  chainId: z12.number().nullable().describe("Chain ID of the anchor transaction"),
  actorIsUserOfRecord: z12.boolean().nullable().describe(
    "Whether the actor who recorded this event is the user-of-record. Null when not applicable (system events)."
  ),
  actorRole: z12.enum(["user_of_record", "other_user", "system", "integration", "unknown"]).describe("Role classification of the actor who recorded this event")
});
var PublicProofSchema = z12.object({
  hash: TxHashSchema.describe("Event hash (leaf)"),
  merkleRoot: z12.string().describe("Merkle root of the batch"),
  proof: z12.array(z12.string()).describe("Sibling hashes from leaf to root"),
  leafIndex: z12.number().describe("Zero-based leaf position in the merkle tree"),
  chainId: z12.number().nullable().describe("Chain ID where the root is anchored"),
  txHash: z12.string().nullable().describe("Anchor transaction hash"),
  blockNumber: z12.string().nullable().describe("Block number of the anchor transaction"),
  blockTimestamp: z12.string().datetime().nullable().describe("ISO timestamp of the anchor block")
});
var PublicEventDetailSchema = PublicEventSchema.merge(
  PublicProofSchema.pick({
    proof: true,
    leafIndex: true,
    txHash: true,
    blockNumber: true,
    blockTimestamp: true
  })
);
var RespaServiceStatusSchema = z12.object({
  code: RESPAServiceCodeSchema.describe("RESPA Section 8 service code"),
  name: z12.string().describe("Human-readable RESPA service name"),
  completed: z12.boolean().describe(
    "True if at least one event with this RESPA code exists for the loan"
  ),
  eventCount: z12.number().describe("Number of events recorded under this service code"),
  lastEventAt: z12.string().datetime().nullable().describe("ISO timestamp of the most recent event for this code")
});
var PublicLoanRespaSchema = z12.object({
  loanIdHash: z12.string().describe("Stable opaque hash of the loan ID"),
  services: z12.array(RespaServiceStatusSchema).describe("One entry per RESPA code, ordered a\u2013m (13 total)"),
  completedCount: z12.number().describe("Number of services where completed=true"),
  totalServices: z12.number().describe("Always 13 \u2014 one per RESPA code a\u2013m"),
  respaCompliant: z12.boolean().describe(
    "True when completedCount is 5 or more (platform compliance threshold)"
  )
});
var PublicLoanAuditSummarySchema = z12.object({
  loanIdHash: z12.string().describe("Stable opaque hash of the loan ID"),
  eventCount: z12.number().describe("Total number of recorded events"),
  verifiedCount: z12.number().describe("Events that passed merkle proof verification"),
  anchoredCount: z12.number().describe("Events whose batch has been anchored on-chain"),
  pendingCount: z12.number().describe("Events not yet anchored"),
  respaCompliant: z12.boolean().describe("Whether the loan meets the RESPA compliance threshold"),
  respaCompletedCount: z12.number().describe("Number of RESPA service codes with at least one event"),
  firstEventAt: z12.string().datetime().nullable().describe("ISO timestamp of the earliest event"),
  lastEventAt: z12.string().datetime().nullable().describe("ISO timestamp of the most recent event")
});
var PublicBatchSchema = z12.object({
  merkleRoot: z12.string().describe("Merkle root identifying this batch"),
  recordCount: z12.number().describe("Number of event leaves in the batch"),
  createdAt: z12.string().datetime().describe("ISO timestamp when the batch was created"),
  anchored: z12.boolean().describe("Whether this batch has been anchored on-chain"),
  chainId: z12.number().nullable().describe("Chain ID of the anchor transaction"),
  txHash: z12.string().nullable().describe("Anchor transaction hash"),
  blockNumber: z12.string().nullable().describe("Block number of the anchor transaction"),
  blockTimestamp: z12.string().datetime().nullable().describe("ISO timestamp of the anchor block")
});
var PublicBatchDetailSchema = PublicBatchSchema.extend({
  previousBatchRoot: z12.string().nullable().describe(
    "Merkle root of the preceding batch \u2014 used for chain-of-custody checks"
  ),
  nextBatchRoot: z12.string().nullable().describe("Merkle root of the following batch, if it exists")
});
var PublicProofVerifySchema = z12.object({
  hash: TxHashSchema.describe("Event hash to verify (leaf)"),
  proof: z12.array(z12.string()).describe("Sibling hashes from leaf to root"),
  leafIndex: z12.number().int().min(0).describe("Zero-based leaf position in the merkle tree"),
  merkleRoot: z12.string().describe("Expected merkle root")
});
var PublicProofVerifyResultSchema = z12.object({
  valid: z12.boolean().describe("Whether the computed root matches the expected root"),
  computedRoot: z12.string().describe(
    "The merkle root derived from the supplied proof \u2014 returned for transparency"
  ),
  expectedRoot: z12.string().describe("The merkle root supplied in the request")
});
var PublicBulkVerifySchema = z12.object({
  hashes: z12.array(TxHashSchema).min(1).max(100).describe("Between 1 and 100 event hashes to check")
});
var PublicBulkVerifyResultSchema = z12.object({
  results: z12.array(
    z12.object({
      hash: TxHashSchema,
      verified: z12.boolean(),
      anchored: z12.boolean(),
      merkleRoot: z12.string().nullable()
    })
  ),
  summary: z12.object({
    total: z12.number(),
    verified: z12.number(),
    failed: z12.number()
  })
});
var PublicChainStatusSchema = z12.object({
  chainId: z12.number().describe("EVM chain ID"),
  chainName: z12.string().describe("Human-readable chain name"),
  connected: z12.boolean().describe("Whether the platform can currently reach this chain"),
  latestBlock: z12.string().nullable().describe("Most recently observed block number"),
  lastAnchorAt: z12.string().datetime().nullable().describe("ISO timestamp of the most recent anchor recorded on this chain"),
  pendingAnchors: z12.number().describe("Number of batches awaiting anchoring on this chain")
});
var PublicSystemHealthSchema = z12.object({
  chains: z12.array(PublicChainStatusSchema),
  totalBatches: z12.number().describe("Total number of batches ever created"),
  anchoredBatches: z12.number().describe("Batches that have been anchored on-chain"),
  pendingBatches: z12.number().describe("Batches not yet anchored"),
  oldestPendingBatchAge: z12.number().nullable().describe(
    "Age in seconds of the oldest unanchored batch \u2014 anchoring lag indicator"
  ),
  lastBatchCreatedAt: z12.string().datetime().nullable().describe("ISO timestamp of the most recently created batch"),
  lastBatchAnchoredAt: z12.string().datetime().nullable().describe("ISO timestamp of the most recently anchored batch"),
  eventVolumeLast30d: z12.array(
    z12.object({
      date: z12.string().date().describe("Calendar date (YYYY-MM-DD)"),
      count: z12.number().int().describe("Number of events recorded on this date")
    })
  ).describe("Daily event counts for the trailing 30 calendar days")
});
var PublicCertificateRequestSchema = z12.object({
  kind: z12.enum(["event", "loan", "batch"]).describe("The type of subject to certify"),
  target: z12.string().describe(
    "The event hash, loan ID, or batch merkle root \u2014 discriminated by kind"
  ),
  includeProof: z12.boolean().default(true).describe("Whether to embed the merkle proof in the generated certificate"),
  dateFrom: z12.string().date().nullable().optional().describe("Start of date range for scoped certificate (YYYY-MM-DD)"),
  dateTo: z12.string().date().nullable().optional().describe("End of date range for scoped certificate (YYYY-MM-DD)")
});
var PublicCertificateMetadataSchema = z12.unknown();
var PublicIntegrityAlertTypeSchema = IntegrityAlertTypeSchema;
var PublicIntegrityAlertSchema = z12.object({
  id: z12.string().describe("Opaque alert identifier (HMAC-hashed, not raw cuid)"),
  alertType: PublicIntegrityAlertTypeSchema,
  severity: AlertSeveritySchema,
  loanCaseIdHash: z12.string().nullable().describe("HMAC-hashed LoanCase id \u2014 raw id never exposed"),
  batchRoot: z12.string().nullable().describe("Merkle root of the affected batch \u2014 already public"),
  chainId: z12.number().int().nullable().describe("Chain ID if applicable"),
  eventType: z12.string().nullable().describe("Event type that triggered the alert"),
  expectedCreatorActorIdHash: z12.string().nullable().describe(
    "HMAC-hashed expected creator actor id \u2014 set when actor differs from creatorActorId"
  ),
  expectedBankerActorIdHash: z12.string().nullable().describe(
    "HMAC-hashed expected banker-of-record actor id \u2014 set when actor differs from primaryBankerActorId"
  ),
  actualActorIdHash: z12.string().nullable().describe("HMAC-hashed actual actor id"),
  createdAt: z12.string().datetime()
});
var PublicIntegrityAlertsResponseSchema = z12.object({
  items: z12.array(PublicIntegrityAlertSchema),
  pagination: z12.object({
    total: z12.number().int(),
    limit: z12.number().int(),
    offset: z12.number().int()
  })
});
var PublicCsvReportRequestSchema = z12.object({
  kind: z12.enum(["event", "loan", "batch"]).describe("Report subject type"),
  target: z12.string().min(1).describe("Event hash, loanCaseId, or batch root"),
  dateFrom: z12.string().date().nullable().optional().describe("Start date filter (YYYY-MM-DD)"),
  dateTo: z12.string().date().nullable().optional().describe("End date filter (YYYY-MM-DD)")
}).strict();

// src/contracts/public-verification.contract.ts
var c5 = initContract5();
var publicVerificationContract = c5.router({
  /**
   * Retrieve anonymized event metadata by cryptographic hash.
   */
  getEventByHash: {
    method: "GET",
    path: "/public/events/:hash",
    pathParams: z13.object({
      hash: TxHashSchema
    }),
    responses: {
      200: PublicEventDetailSchema,
      404: ErrorResponseSchema
    },
    summary: "Get event by hash",
    description: "Returns anonymized event metadata and merkle proof for a given event hash. No PII is included in the response."
  },
  /**
   * Paginated list of recent events across all loans (anonymized).
   */
  listEvents: {
    method: "GET",
    path: "/public/events",
    query: PaginationQuerySchema,
    responses: {
      200: PaginatedResponseSchema(PublicEventSchema)
    },
    summary: "List recent events",
    description: "Returns a paginated, anonymized list of recent events across all loans. Intended as a default view for the Events page when no hash is supplied."
  },
  /**
   * Paginated event timeline for a loan.
   */
  getLoanTimeline: {
    method: "GET",
    path: "/public/loans/:loanId/timeline",
    pathParams: z13.object({
      loanId: z13.string().describe("Opaque loan identifier provided out-of-band by the lender")
    }),
    query: PaginationQuerySchema.extend({
      eventType: z13.string().optional().describe("Filter by event type enum string"),
      respaCode: RESPAServiceCodeSchema.optional().describe(
        "Filter by RESPA service code"
      )
    }),
    responses: {
      200: PaginatedResponseSchema(PublicEventSchema),
      404: ErrorResponseSchema
    },
    summary: "Get loan event timeline",
    description: "Returns a paginated, chronological list of anonymized events for the specified loan. The loanId path parameter is never reflected in the response body; responses use loanIdHash."
  },
  /**
   * RESPA compliance status across all 13 service codes.
   */
  getLoanRespa: {
    method: "GET",
    path: "/public/loans/:loanId/respa",
    pathParams: z13.object({
      loanId: z13.string().describe("Opaque loan identifier provided out-of-band by the lender")
    }),
    responses: {
      200: PublicLoanRespaSchema,
      404: ErrorResponseSchema
    },
    summary: "Get loan RESPA compliance",
    description: "Returns RESPA Section 8 completion status for each of the 13 service codes (a\u2013m). Includes aggregate compliance flag based on the platform threshold of 5 or more completed services."
  },
  /**
   * Audit summary counts for a loan.
   */
  getLoanAuditSummary: {
    method: "GET",
    path: "/public/loans/:loanId/audit-summary",
    pathParams: z13.object({
      loanId: z13.string().describe("Opaque loan identifier provided out-of-band by the lender")
    }),
    responses: {
      200: PublicLoanAuditSummarySchema,
      404: ErrorResponseSchema
    },
    summary: "Get loan audit summary",
    description: "Returns aggregate event counts \u2014 total, verified, anchored, pending \u2014 and RESPA compliance status for the specified loan. Contains no PII."
  },
  /**
   * Paginated list of merkle batches.
   */
  getBatches: {
    method: "GET",
    path: "/public/batches",
    query: PaginationQuerySchema.extend({
      chainId: z13.coerce.number().optional().describe("Filter batches by chain ID"),
      anchored: z13.enum(["true", "false"]).optional().describe(
        'Filter by anchoring status: "true" for anchored, "false" for pending'
      )
    }),
    responses: {
      200: PaginatedResponseSchema(PublicBatchSchema)
    },
    summary: "List merkle batches",
    description: "Returns a paginated list of merkle batches with anchor metadata. Supports filtering by chain and anchoring status."
  },
  /**
   * Single batch detail with chain linkage.
   */
  getBatchDetail: {
    method: "GET",
    path: "/public/batches/:root",
    pathParams: z13.object({
      root: z13.string().describe("Merkle root identifying the batch")
    }),
    responses: {
      200: PublicBatchDetailSchema,
      404: ErrorResponseSchema
    },
    summary: "Get batch detail",
    description: "Returns full anchor metadata for a batch identified by its merkle root, including links to the previous and next batches for chain-of-custody verification."
  },
  /**
   * Paginated event list for a single batch.
   */
  getBatchEvents: {
    method: "GET",
    path: "/public/batches/:root/events",
    pathParams: z13.object({ root: z13.string() }),
    query: PaginationQuerySchema,
    responses: {
      200: PaginatedResponseSchema(PublicEventSchema),
      404: ErrorResponseSchema
    },
    summary: "Get events in a batch",
    description: "Returns the anonymized event timeline for a single anchored batch."
  },
  /**
   * Client-side merkle proof verification.
   */
  verifyProof: {
    method: "POST",
    path: "/public/verify/proof",
    body: PublicProofVerifySchema,
    responses: {
      200: PublicProofVerifyResultSchema
    },
    summary: "Verify merkle proof",
    description: "Accepts an event hash, sibling proof array, leaf index, and expected merkle root, then computes and returns whether the proof is valid. The computed root is included in the response for transparency."
  },
  /**
   * Bulk hash status check.
   */
  verifyBulk: {
    method: "POST",
    path: "/public/verify/bulk",
    body: PublicBulkVerifySchema,
    responses: {
      200: PublicBulkVerifyResultSchema
    },
    summary: "Bulk hash verification",
    description: "Accepts 1\u2013100 event hashes and returns verification and anchoring status for each, along with an aggregate summary."
  },
  /**
   * List all configured chains.
   */
  getChains: {
    method: "GET",
    path: "/public/chains",
    responses: {
      200: z13.object({
        chains: z13.array(PublicChainStatusSchema)
      })
    },
    summary: "List chains",
    description: "Returns public status for all configured blockchain connections. Internal details such as RPC URL are omitted."
  },
  /**
   * Platform-wide anchoring health.
   */
  getSystemHealth: {
    method: "GET",
    path: "/public/system/health",
    responses: {
      200: PublicSystemHealthSchema
    },
    summary: "System health",
    description: "Returns aggregate batch counts, per-chain anchoring status, and 30-day event volume. Includes the age of the oldest unanchored batch as a lag indicator."
  },
  /**
   * Generate a PDF audit certificate.
   */
  generateCertificate: {
    method: "POST",
    path: "/public/reports/certificate",
    body: PublicCertificateRequestSchema,
    responses: {
      200: PublicCertificateMetadataSchema,
      404: ErrorResponseSchema
    },
    summary: "Generate audit certificate",
    description: 'Generates a cryptographic audit certificate for an event, loan, or batch. The response is a binary PDF stream (Content-Type: application/pdf). The kind field discriminates the target: "event" uses an event hash, "loan" uses a loan ID, "batch" uses a merkle root.'
  },
  /**
   * Paginated public integrity alerts — no API key required.
   */
  getIntegrityAlerts: {
    method: "GET",
    path: "/public/integrity/alerts",
    query: z13.object({
      since: z13.string().datetime().optional().describe("Filter alerts after this ISO timestamp"),
      severity: AlertSeveritySchema.optional().describe(
        "Filter by severity level"
      ),
      limit: z13.coerce.number().int().min(1).max(100).default(20).describe("Max results (default 20, max 100)"),
      offset: z13.coerce.number().int().min(0).default(0).describe("Results to skip")
    }),
    responses: {
      200: PublicIntegrityAlertsResponseSchema
    },
    summary: "List public integrity alerts",
    description: "Returns a paginated list of integrity alerts. No API key required. Alert IDs and actor IDs are HMAC-hashed; raw identifiers are never exposed."
  },
  /**
   * CSV data report download.
   *
   * NOTE: ts-rest cannot model binary streams natively. The response is typed
   * as z.unknown() here. The actual HTTP response body is text/csv. Consumers
   * must handle the raw response stream rather than parsing the body as JSON.
   */
  downloadCsvReport: {
    method: "POST",
    path: "/public/reports/data.csv",
    body: PublicCsvReportRequestSchema,
    responses: {
      200: z13.unknown(),
      404: ErrorResponseSchema
    },
    summary: "Download CSV report",
    description: "Streams a CSV data report for an event, loan, or batch. Content-Type: text/csv. Supports optional date range scoping."
  },
  /**
   * Paginated event timeline resolved by external loan ID + application stage.
   * Delegates to the same logic as getLoanTimeline after resolving the cuid.
   */
  getLoanTimelineByExternal: {
    method: "GET",
    path: "/public/loans/by-external/:externalLoanId/:applicationStage/timeline",
    pathParams: z13.object({
      externalLoanId: z13.string().describe("External loan identifier assigned by the lender's LOS"),
      applicationStage: ApplicationStageSchema.describe(
        "Application stage discriminator: PRE_APPROVAL or LOAN_APPLICATION"
      )
    }),
    query: PaginationQuerySchema.extend({
      eventType: z13.string().optional().describe("Filter by event type enum string"),
      respaCode: RESPAServiceCodeSchema.optional().describe(
        "Filter by RESPA service code"
      )
    }),
    responses: {
      200: PaginatedResponseSchema(PublicEventSchema),
      404: ErrorResponseSchema
    },
    summary: "Get loan event timeline by external ID",
    description: "Resolves a loan case by (externalLoanId, applicationStage) and returns the same paginated, anonymized event timeline as getLoanTimeline. Returns 404 when no matching case exists."
  },
  /**
   * RESPA compliance status resolved by external loan ID + application stage.
   */
  getLoanRespaByExternal: {
    method: "GET",
    path: "/public/loans/by-external/:externalLoanId/:applicationStage/respa",
    pathParams: z13.object({
      externalLoanId: z13.string().describe("External loan identifier assigned by the lender's LOS"),
      applicationStage: ApplicationStageSchema.describe(
        "Application stage discriminator: PRE_APPROVAL or LOAN_APPLICATION"
      )
    }),
    responses: {
      200: PublicLoanRespaSchema,
      404: ErrorResponseSchema
    },
    summary: "Get loan RESPA compliance by external ID",
    description: "Resolves a loan case by (externalLoanId, applicationStage) and returns the same RESPA compliance summary as getLoanRespa. Returns 404 when no matching case exists."
  },
  /**
   * Audit summary counts resolved by external loan ID + application stage.
   */
  getLoanAuditSummaryByExternal: {
    method: "GET",
    path: "/public/loans/by-external/:externalLoanId/:applicationStage/audit-summary",
    pathParams: z13.object({
      externalLoanId: z13.string().describe("External loan identifier assigned by the lender's LOS"),
      applicationStage: ApplicationStageSchema.describe(
        "Application stage discriminator: PRE_APPROVAL or LOAN_APPLICATION"
      )
    }),
    responses: {
      200: PublicLoanAuditSummarySchema,
      404: ErrorResponseSchema
    },
    summary: "Get loan audit summary by external ID",
    description: "Resolves a loan case by (externalLoanId, applicationStage) and returns the same aggregate audit summary as getLoanAuditSummary. Returns 404 when no matching case exists."
  }
});

// src/contracts/verification.contract.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z14 } from "zod";
var c6 = initContract6();
var verificationContract = c6.router({
  /**
   * Verify an event by ID
   */
  verifyById: {
    method: "POST",
    path: "/verify/event",
    body: VerifyEventSchema,
    responses: {
      200: VerificationResultSchema,
      404: ErrorResponseSchema
    },
    summary: "Verify event by ID",
    description: "Verifies an event using its merkle proof and on-chain anchor"
  },
  /**
   * Verify by hash
   */
  verifyByHash: {
    method: "POST",
    path: "/verify/hash",
    body: VerifyByHashSchema,
    responses: {
      200: VerificationResultSchema,
      404: ErrorResponseSchema
    },
    summary: "Verify by hash",
    description: "Verifies a hash exists and returns its proof"
  },
  /**
   * Batch verification
   */
  batchVerify: {
    method: "POST",
    path: "/verify/batch",
    body: BatchVerifySchema,
    responses: {
      200: BatchVerificationResultSchema
    },
    summary: "Batch verification",
    description: "Verifies multiple hashes in a single request"
  },
  /**
   * Get chain status
   */
  getChainStatus: {
    method: "GET",
    path: "/verify/chains",
    responses: {
      200: z14.object({
        chains: z14.array(ChainStatusSchema)
      })
    },
    summary: "Get chain status",
    description: "Returns status of all configured blockchain connections"
  },
  /**
   * Get specific chain status
   */
  getChainById: {
    method: "GET",
    path: "/verify/chains/:chainId",
    pathParams: z14.object({
      chainId: z14.string()
    }),
    responses: {
      200: ChainStatusSchema,
      404: ErrorResponseSchema
    },
    summary: "Get chain by ID",
    description: "Returns status of a specific blockchain connection"
  },
  /**
   * Generate audit trail report
   */
  getAuditTrail: {
    method: "GET",
    path: "/verify/audit/:loanCaseId",
    pathParams: z14.object({
      loanCaseId: z14.string()
    }),
    responses: {
      200: AuditTrailSchema,
      404: ErrorResponseSchema
    },
    summary: "Get audit trail",
    description: "Generates a complete verification report for a LoanCase"
  },
  /**
   * Verify merkle root on-chain
   */
  verifyOnChain: {
    method: "POST",
    path: "/verify/on-chain",
    body: z14.object({
      merkleRoot: z14.string().regex(/^0x[a-fA-F0-9]{64}$/),
      chainId: z14.number()
    }).strict(),
    responses: {
      200: z14.object({
        verified: z14.boolean(),
        merkleRoot: z14.string(),
        chainId: z14.number(),
        chainName: z14.string(),
        txHash: z14.string().nullable(),
        blockNumber: z14.string().nullable(),
        timestamp: z14.string().datetime().nullable()
      }),
      404: ErrorResponseSchema
    },
    summary: "Verify on-chain",
    description: "Checks if a merkle root exists on the specified chain"
  },
  /**
   * RFC 6962 inclusion proof for an event (HOM-48)
   */
  getEventInclusionProof: {
    method: "GET",
    path: "/verification/events/:eventId/proof",
    pathParams: EventProofParamsSchema,
    responses: {
      200: EventInclusionProofSchema,
      404: ErrorResponseSchema
    },
    summary: "Get event inclusion proof",
    description: "Returns the RFC 6962 merkle inclusion proof for an event, including its leaf index, ordered sibling path, and all on-chain anchors for the batch. Returns 404 if the event does not exist or has not yet been included in a merkle batch."
  },
  /**
   * RFC 6962 consistency proof between two batches (HOM-48)
   */
  getBatchConsistencyProof: {
    method: "GET",
    path: "/verification/batches/:batchId/consistency/:olderBatchId",
    pathParams: ConsistencyProofParamsSchema,
    responses: {
      200: ConsistencyProofResponseSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      422: ErrorResponseSchema
    },
    summary: "Get batch consistency proof",
    description: "Returns an RFC 6962 consistency proof demonstrating that an older batch is a prefix of a newer batch within the same chain lineage. Returns 400 if the batches are not in the same chain, 404 if either batch does not exist, and 422 if the batches use incompatible hash algorithms."
  },
  /**
   * Paginated integrity alert list (HOM-48)
   */
  getIntegrityAlerts: {
    method: "GET",
    path: "/verification/integrity/alerts",
    query: IntegrityAlertsQuerySchema,
    responses: {
      200: IntegrityAlertsResponseSchema
    },
    summary: "List integrity alerts",
    description: "Returns a paginated list of integrity alerts raised by tamper-detection checks. Supports filtering by resolved status and severity. Limit defaults to 50 with a maximum of 250."
  },
  /**
   * Look up a batch by its on-chain anchor tx (HOM-48)
   */
  getAnchorByTx: {
    method: "GET",
    path: "/verification/anchors/:chainId/:txHash",
    pathParams: AnchorLookupParamsSchema,
    responses: {
      200: AnchorLookupResponseSchema,
      404: ErrorResponseSchema
    },
    summary: "Look up anchor by transaction",
    description: "Resolves a (chainId, txHash) pair to the corresponding PublicAnchor record and its associated MerkleBatch. Returns 404 if no anchor matches."
  }
});

// src/router.ts
import { initContract as initContract7 } from "@ts-rest/core";
var c7 = initContract7();
var apiContract = c7.router(
  {
    health: healthContract,
    loanCases: loanCaseContract,
    events: eventsContract,
    verification: verificationContract,
    publicVerification: publicVerificationContract,
    approvals: approvalsContract
  },
  {
    pathPrefix: "/api"
  }
);

// src/schemas/event-payloads.ts
import { z as z15 } from "zod";
var LoanTypeSchema = z15.enum([
  "CONVENTIONAL",
  "FHA",
  "VA",
  "USDA",
  "JUMBO",
  "OTHER"
]);
var EmploymentStatusSchema = z15.enum([
  "EMPLOYED",
  "SELF_EMPLOYED",
  "RETIRED",
  "UNEMPLOYED",
  "OTHER"
]);
var DocumentTypeSchema = z15.enum([
  "PAYSTUB",
  "W2",
  "TAX_RETURN",
  "BANK_STATEMENT",
  "APPRAISAL",
  "TITLE",
  "INSURANCE",
  "ID",
  "CREDIT_REPORT",
  "OTHER"
]);
var VerificationStatusSchema = z15.enum([
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "REJECTED"
]);
var MonetaryAmountSchema = z15.string().regex(/^\d+(\.\d{1,2})?$/, "Must be a valid monetary amount");
var ApplicationSubmittedPayloadSchema = z15.object({
  applicationNumber: z15.string().min(1).max(50).describe("Unique application identifier"),
  borrowerName: z15.string().min(1).max(200).describe("Primary borrower name"),
  coBorrowerName: z15.string().max(200).optional().describe("Co-borrower name if applicable"),
  loanAmount: MonetaryAmountSchema.describe("Requested loan amount"),
  propertyAddress: z15.string().min(1).max(500).describe("Property address"),
  loanType: LoanTypeSchema.describe("Type of loan"),
  loanPurpose: z15.enum(["PURCHASE", "REFINANCE", "CASH_OUT_REFINANCE", "OTHER"]).describe("Purpose of the loan")
});
var ApplicationUpdatedPayloadSchema = z15.object({
  applicationNumber: z15.string().min(1).max(50),
  fieldsUpdated: z15.array(z15.string()).min(1).describe("List of updated fields"),
  previousValues: z15.record(z15.unknown()).optional().describe("Previous values for audit"),
  newValues: z15.record(z15.unknown()).describe("New values"),
  updateReason: z15.string().max(500).optional().describe("Reason for update")
});
var RuncapApiPulledPayloadSchema = z15.object({
  requestedAmount: MonetaryAmountSchema.describe("Requested loan amount"),
  estimatedIncome: MonetaryAmountSchema.optional().describe("Annual income"),
  estimatedCreditScore: z15.number().int().min(300).max(850).optional().describe("Estimated credit score"),
  propertyType: z15.enum(["SINGLE_FAMILY", "CONDO", "TOWNHOUSE", "MULTI_FAMILY", "OTHER"]).optional()
});
var PrequalificationCompletedPayloadSchema = z15.object({
  approvedAmount: MonetaryAmountSchema.describe("Approved loan amount"),
  expirationDate: z15.string().datetime().describe("Prequalification expiration"),
  conditions: z15.array(z15.string()).optional().describe("Conditions for approval"),
  letterIssued: z15.boolean().default(false).describe("Prequalification letter issued")
});
var DocumentRequestedPayloadSchema = z15.object({
  documentType: DocumentTypeSchema.describe("Type of document requested"),
  description: z15.string().max(500).describe("Document description"),
  dueDate: z15.string().datetime().optional().describe("Due date for document"),
  requestedBy: z15.string().max(100).describe("Person/system requesting"),
  isRequired: z15.boolean().default(true).describe("Whether document is required")
});
var DocumentReceivedPayloadSchema = z15.object({
  documentType: DocumentTypeSchema.describe("Type of document received"),
  fileName: z15.string().max(255).describe("Original filename"),
  fileSize: z15.number().int().positive().describe("File size in bytes"),
  mimeType: z15.string().max(100).describe("MIME type"),
  uploadedBy: z15.string().max(100).describe("Person who uploaded"),
  storageKey: z15.string().max(500).optional().describe("Storage location key")
});
var DocumentVerifiedPayloadSchema = z15.object({
  documentType: DocumentTypeSchema.describe("Type of document verified"),
  verifiedBy: z15.string().max(100).describe("Person who verified"),
  verificationDate: z15.string().datetime().describe("Verification timestamp"),
  verificationNotes: z15.string().max(1e3).optional().describe("Verification notes")
});
var DocumentRejectedPayloadSchema = z15.object({
  documentType: DocumentTypeSchema.describe("Type of document rejected"),
  rejectedBy: z15.string().max(100).describe("Person who rejected"),
  rejectionDate: z15.string().datetime().describe("Rejection timestamp"),
  rejectionReason: z15.string().min(1).max(1e3).describe("Reason for rejection"),
  resubmissionRequired: z15.boolean().default(true).describe("Whether resubmission is required")
});
var VoeInitiatedPayloadSchema = z15.object({
  employerName: z15.string().min(1).max(200).describe("Employer name"),
  employerContact: z15.string().max(200).optional().describe("Contact info for verification"),
  jobTitle: z15.string().max(100).optional().describe("Borrower's job title"),
  requestedDate: z15.string().datetime().describe("When verification was requested"),
  verificationMethod: z15.enum(["PHONE", "EMAIL", "THIRD_PARTY", "AUTOMATED", "OTHER"]).optional()
});
var VoeCompletedPayloadSchema = z15.object({
  employerName: z15.string().min(1).max(200).describe("Employer name"),
  employmentStatus: EmploymentStatusSchema.describe("Employment status"),
  verificationDate: z15.string().datetime().describe("Verification completion date"),
  annualIncome: MonetaryAmountSchema.optional().describe(
    "Verified annual income"
  ),
  jobTitle: z15.string().max(100).optional().describe("Verified job title"),
  startDate: z15.string().datetime().optional().describe("Employment start date"),
  isCurrentlyEmployed: z15.boolean().optional().describe("Currently employed status"),
  verifiedBy: z15.string().max(100).optional().describe("Person who verified")
});
var VodInitiatedPayloadSchema = z15.object({
  institutionName: z15.string().min(1).max(200).describe("Financial institution name"),
  accountType: z15.enum(["CHECKING", "SAVINGS", "INVESTMENT", "RETIREMENT", "OTHER"]).describe("Type of account"),
  requestedDate: z15.string().datetime().describe("When verification was requested"),
  verificationMethod: z15.enum(["BANK_STATEMENT", "THIRD_PARTY", "AUTOMATED", "OTHER"]).optional()
});
var VodCompletedPayloadSchema = z15.object({
  institutionName: z15.string().min(1).max(200).describe("Financial institution name"),
  accountType: z15.enum(["CHECKING", "SAVINGS", "INVESTMENT", "RETIREMENT", "OTHER"]).describe("Type of account"),
  verificationDate: z15.string().datetime().describe("Verification completion date"),
  verifiedBalance: MonetaryAmountSchema.optional().describe("Verified balance"),
  accountNumber: z15.string().max(50).optional().describe("Last 4 digits of account"),
  verifiedBy: z15.string().max(100).optional().describe("Person who verified")
});
var OrderCreditApiPulledPayloadSchema = z15.object({
  reportDate: z15.string().datetime().describe("Date report was pulled"),
  bureauName: z15.enum(["EQUIFAX", "EXPERIAN", "TRANSUNION", "TRI_MERGE", "OTHER"]).describe("Credit bureau"),
  creditScore: z15.number().int().min(300).max(850).describe("Credit score"),
  reportId: z15.string().max(100).optional().describe("Report reference ID"),
  pulledBy: z15.string().max(100).describe("Person/system who pulled report"),
  consentDate: z15.string().datetime().optional().describe("Borrower consent date")
});
var LoanCaseVerificationRequestedPayloadSchema = z15.object({
  lenderName: z15.string().min(1).max(200).describe("Current lender name"),
  loanNumber: z15.string().max(50).optional().describe("Current loan number"),
  requestedDate: z15.string().datetime().describe("Request date"),
  propertyAddress: z15.string().max(500).describe("Property address")
});
var LoanCaseVerificationCompletedPayloadSchema = z15.object({
  lenderName: z15.string().min(1).max(200).describe("Current lender name"),
  verificationDate: z15.string().datetime().describe("Verification date"),
  currentBalance: MonetaryAmountSchema.optional().describe(
    "Current loan balance"
  ),
  monthlyPayment: MonetaryAmountSchema.optional().describe(
    "Monthly payment amount"
  ),
  paymentHistory: z15.string().max(500).optional().describe("Payment history summary"),
  verifiedBy: z15.string().max(100).optional().describe("Person who verified")
});
var InspectionOrderedPayloadSchema = z15.object({
  inspectionType: z15.enum(["HOME", "PEST", "STRUCTURAL", "ENVIRONMENTAL", "OTHER"]).describe("Type of inspection"),
  inspectorName: z15.string().max(200).optional().describe("Inspector name"),
  scheduledDate: z15.string().datetime().optional().describe("Scheduled inspection date"),
  orderedBy: z15.string().max(100).describe("Person who ordered"),
  orderDate: z15.string().datetime().describe("Order date")
});
var InspectionCompletedPayloadSchema = z15.object({
  inspectionType: z15.enum(["HOME", "PEST", "STRUCTURAL", "ENVIRONMENTAL", "OTHER"]).describe("Type of inspection"),
  inspectorName: z15.string().max(200).describe("Inspector name"),
  completionDate: z15.string().datetime().describe("Completion date"),
  reportId: z15.string().max(100).optional().describe("Report reference ID"),
  issuesFound: z15.array(z15.string()).optional().describe("List of issues identified"),
  status: z15.enum(["PASSED", "PASSED_WITH_ISSUES", "FAILED", "PENDING_REVIEW"]).describe("Inspection result status")
});
var OrderFloodCertApiPulledPayloadSchema = z15.object({
  propertyAddress: z15.string().min(1).max(500).describe("Property address"),
  floodZone: z15.string().max(10).describe("FEMA flood zone designation"),
  isInFloodZone: z15.boolean().describe("Whether property is in flood zone"),
  determinationDate: z15.string().datetime().describe("Determination date"),
  floodInsuranceRequired: z15.boolean().describe("Whether flood insurance is required"),
  certificateNumber: z15.string().max(50).optional().describe("Flood certificate number"),
  provider: z15.string().max(200).optional().describe("Determination provider")
});
var DisclosureSentPayloadSchema = z15.object({
  disclosureType: z15.enum(["GFE", "TILA", "RESPA", "CLOSING_DISCLOSURE", "OTHER"]).describe("Type of disclosure"),
  sentDate: z15.string().datetime().describe("Date sent"),
  recipientEmail: z15.string().email().optional().describe("Recipient email"),
  deliveryMethod: z15.enum(["EMAIL", "MAIL", "IN_PERSON", "PORTAL", "OTHER"]).describe("Delivery method"),
  documentId: z15.string().max(100).optional().describe("Document reference ID"),
  sentBy: z15.string().max(100).describe("Person/system who sent")
});
var DisclosureAcknowledgedPayloadSchema = z15.object({
  disclosureType: z15.enum(["GFE", "TILA", "RESPA", "CLOSING_DISCLOSURE", "OTHER"]).describe("Type of disclosure"),
  acknowledgedDate: z15.string().datetime().describe("Acknowledgment date"),
  acknowledgedBy: z15.string().max(100).describe("Person who acknowledged"),
  signatureMethod: z15.enum(["ELECTRONIC", "WET_SIGNATURE", "VERBAL", "OTHER"]).optional().describe("Signature method"),
  ipAddress: z15.string().max(45).optional().describe("IP address of acknowledgment")
});
var GfeProvidedPayloadSchema = z15.object({
  providedDate: z15.string().datetime().describe("Date GFE was provided"),
  estimatedClosingCosts: MonetaryAmountSchema.describe(
    "Estimated closing costs"
  ),
  estimatedRate: z15.string().regex(/^\d+\.\d{3}$/).describe("Estimated interest rate (e.g., 6.500)"),
  estimatedPayment: MonetaryAmountSchema.describe("Estimated monthly payment"),
  expirationDate: z15.string().datetime().optional().describe("GFE expiration date"),
  documentId: z15.string().max(100).optional().describe("Document reference ID")
});
var TilaProvidedPayloadSchema = z15.object({
  providedDate: z15.string().datetime().describe("Date TILA was provided"),
  apr: z15.string().regex(/^\d+\.\d{3}$/).describe("Annual Percentage Rate (e.g., 6.750)"),
  financeCharge: MonetaryAmountSchema.describe("Total finance charge"),
  amountFinanced: MonetaryAmountSchema.describe("Amount financed"),
  totalOfPayments: MonetaryAmountSchema.describe("Total of all payments"),
  documentId: z15.string().max(100).optional().describe("Document reference ID")
});
var BorrowerCounseledPayloadSchema = z15.object({
  counselingDate: z15.string().datetime().describe("Counseling date"),
  counselorName: z15.string().max(200).describe("Counselor name"),
  counselingType: z15.enum([
    "HOMEBUYER_EDUCATION",
    "CREDIT_COUNSELING",
    "FORECLOSURE_PREVENTION",
    "FINANCIAL_LITERACY",
    "OTHER"
  ]).describe("Type of counseling"),
  duration: z15.number().int().positive().optional().describe("Duration in minutes"),
  certificateIssued: z15.boolean().default(false).describe("Whether certificate was issued"),
  certificateId: z15.string().max(100).optional().describe("Certificate reference ID")
});
var OrderCreditUpdatedPayloadSchema = z15.object({
  assistanceDate: z15.string().datetime().describe("Assistance date"),
  assistanceType: z15.enum([
    "CREDIT_REPAIR",
    "DEBT_CONSOLIDATION",
    "PAYMENT_PLAN",
    "DISPUTE_RESOLUTION",
    "OTHER"
  ]).describe("Type of assistance"),
  providedBy: z15.string().max(100).describe("Person/organization who provided"),
  expectedImprovement: z15.string().max(500).optional().describe("Expected credit improvement"),
  followUpRequired: z15.boolean().default(false).describe("Whether follow-up is required")
});
var NoteAddedPayloadSchema = z15.object({
  contactDate: z15.string().datetime().describe("Contact date"),
  contactMethod: z15.enum(["PHONE", "EMAIL", "SMS", "IN_PERSON", "PORTAL", "OTHER"]).describe("Contact method"),
  contactedBy: z15.string().max(100).describe("Person who made contact"),
  purpose: z15.string().max(500).describe("Purpose of contact"),
  outcome: z15.string().max(1e3).optional().describe("Outcome of contact"),
  followUpNeeded: z15.boolean().default(false).describe("Whether follow-up is needed"),
  followUpDate: z15.string().datetime().optional().describe("Follow-up date if needed")
});
var OrderTitleApiPulledPayloadSchema = z15.object({
  orderDate: z15.string().datetime().describe("Order date"),
  titleCompany: z15.string().min(1).max(200).describe("Title company name"),
  propertyAddress: z15.string().min(1).max(500).describe("Property address"),
  orderedBy: z15.string().max(100).describe("Person who ordered"),
  orderType: z15.enum(["SEARCH_ONLY", "SEARCH_AND_INSURANCE", "UPDATE", "OTHER"]).describe("Type of title order"),
  orderNumber: z15.string().max(50).optional().describe("Title order number")
});
var ClosingScheduledPayloadSchema = z15.object({
  scheduledDate: z15.string().datetime().describe("Scheduled closing date"),
  scheduledTime: z15.string().optional().describe("Scheduled time (HH:MM)"),
  location: z15.string().max(500).describe("Closing location"),
  closingAgent: z15.string().max(200).optional().describe("Closing agent name"),
  attendees: z15.array(z15.string()).optional().describe("List of expected attendees"),
  scheduledBy: z15.string().max(100).describe("Person who scheduled"),
  notes: z15.string().max(1e3).optional().describe("Additional notes")
});
var ClosingDocumentsOrderedPayloadSchema = z15.object({
  orderDate: z15.string().datetime().describe("Order date"),
  documentProvider: z15.string().max(200).describe("Document provider"),
  expectedDeliveryDate: z15.string().datetime().optional().describe("Expected delivery date"),
  deliveryMethod: z15.enum(["EMAIL", "MAIL", "COURIER", "ELECTRONIC", "OTHER"]).describe("Delivery method"),
  orderedBy: z15.string().max(100).describe("Person who ordered")
});
var LoanClosedPayloadSchema = z15.object({
  closingDate: z15.string().datetime().describe("Actual closing date"),
  finalLoanAmount: MonetaryAmountSchema.describe("Final loan amount"),
  finalInterestRate: z15.string().regex(/^\d+\.\d{3}$/).describe("Final interest rate (e.g., 6.500)"),
  totalClosingCosts: MonetaryAmountSchema.describe("Total closing costs"),
  closingAgent: z15.string().max(200).describe("Closing agent name"),
  recordedDate: z15.string().datetime().optional().describe("Date documents were recorded"),
  confirmationNumber: z15.string().max(100).optional().describe("Closing confirmation number")
});
var LoanFundedPayloadSchema = z15.object({
  fundingDate: z15.string().datetime().describe("Funding date"),
  fundedAmount: MonetaryAmountSchema.describe("Amount funded"),
  fundingMethod: z15.enum(["WIRE", "ACH", "CHECK", "OTHER"]).describe("Funding method"),
  confirmationNumber: z15.string().max(100).optional().describe("Funding confirmation number"),
  fundedBy: z15.string().max(100).describe("Person/system who funded"),
  disbursementDetails: z15.record(z15.unknown()).optional().describe("Disbursement details")
});
var LoanCaseCreatedPayloadSchema = z15.object({
  loanCaseNumber: z15.string().max(50).optional(),
  loanAmount: MonetaryAmountSchema.optional(),
  loanType: LoanTypeSchema.optional(),
  propertyAddress: z15.string().max(500).optional(),
  createdBy: z15.string().max(100).optional()
});
var LoanCaseActivatedPayloadSchema = z15.object({
  activationDate: z15.string().datetime().optional(),
  activatedBy: z15.string().max(100).optional(),
  status: z15.string().max(50).optional()
});
var PaymentReceivedPayloadSchema = z15.object({
  paymentAmount: MonetaryAmountSchema.optional(),
  paymentDate: z15.string().datetime().optional(),
  paymentMethod: z15.string().max(50).optional(),
  confirmationNumber: z15.string().max(100).optional()
});
var StatusChangedPayloadSchema = z15.object({
  previousStatus: z15.string().max(50).optional(),
  newStatus: z15.string().max(50).optional(),
  reason: z15.string().max(500).optional(),
  changedBy: z15.string().max(100).optional()
});
var DocumentUploadedPayloadSchema = z15.object({
  documentId: z15.string().max(100).describe("Document identifier"),
  documentType: z15.string().max(50).describe("Type of document"),
  uploadedBy: z15.string().max(100).describe("Actor who uploaded the document"),
  fileName: z15.string().max(255).optional().describe("Original filename"),
  fileSize: z15.number().int().positive().optional().describe("File size in bytes"),
  uploadedAt: z15.string().datetime().optional().describe("Upload timestamp")
});
var DocumentDownloadedPayloadSchema = z15.object({
  documentId: z15.string().max(100).describe("Document identifier"),
  documentType: z15.string().max(50).describe("Type of document"),
  downloadedBy: z15.string().max(100).describe("Actor who downloaded the document"),
  downloadedAt: z15.string().datetime().optional().describe("Download timestamp")
});
var DocumentDeletedPayloadSchema = z15.object({
  documentId: z15.string().max(100).describe("Document identifier"),
  documentType: z15.string().max(50).describe("Type of document"),
  deletedBy: z15.string().max(100).describe("Actor who deleted the document"),
  deletedAt: z15.string().datetime().optional().describe("Deletion timestamp")
});
var OrderAppraisalApiPulledPayloadSchema = z15.object({
  appraisedValue: MonetaryAmountSchema.optional(),
  appraisalDate: z15.string().datetime().optional(),
  appraiserName: z15.string().max(200).optional(),
  reportId: z15.string().max(100).optional()
});
var OrderPmiApiPulledPayloadSchema = z15.object({
  termsModified: z15.array(z15.string()).optional(),
  previousValues: z15.record(z15.unknown()).optional(),
  newValues: z15.record(z15.unknown()).optional(),
  modifiedBy: z15.string().max(100).optional()
});
var AnchoredOnChainPayloadSchema = z15.object({
  chainId: z15.number().int().optional(),
  txHash: z15.string().max(66).optional(),
  blockNumber: z15.string().optional(),
  merkleRoot: z15.string().max(66).optional()
});

// src/index.ts
var API_CONTRACT_VERSION = "0.0.0";
export {
  ACTOR_TYPE_DESCRIPTIONS,
  API_CONTRACT_VERSION,
  ActorSchema,
  ActorTypeSchema,
  AlertSeveritySchema,
  AnchorLookupParamsSchema,
  AnchorLookupResponseSchema,
  AnchoredOnChainPayloadSchema,
  ApplicationStageSchema,
  ApplicationSubmittedPayloadSchema,
  ApplicationUpdatedPayloadSchema,
  ApprovalDecisionSchema,
  ApprovalsQuerySchema,
  AuditTrailSchema,
  BatchEventsSchema,
  BatchVerificationResultSchema,
  BatchVerifySchema,
  BorrowerCounseledPayloadSchema,
  COMMON_DEPARTMENTS,
  ChainStatusSchema,
  ClosingDocumentsOrderedPayloadSchema,
  ClosingScheduledPayloadSchema,
  ConsistencyProofParamsSchema,
  ConsistencyProofResponseSchema,
  CreateEventSchema,
  CreateLoanCaseSchema,
  CuidSchema,
  DecimalStringSchema,
  DepartmentSignatureSchema,
  DisclosureAcknowledgedPayloadSchema,
  DisclosureSentPayloadSchema,
  DocumentDeletedPayloadSchema,
  DocumentDownloadedPayloadSchema,
  DocumentReceivedPayloadSchema,
  DocumentRejectedPayloadSchema,
  DocumentRequestedPayloadSchema,
  DocumentTypeSchema,
  DocumentUploadedPayloadSchema,
  DocumentVerifiedPayloadSchema,
  EVENT_TO_RESPA_CODE,
  EVENT_TYPES,
  EmploymentStatusSchema,
  EnhancedEventSchema,
  ErrorResponseSchema,
  EthereumAddressSchema,
  EventInclusionProofSchema,
  EventMetadataSchema,
  EventProofParamsSchema,
  EventQuerySchema,
  EventSchema,
  EventTypeSchema,
  EventWithProofSchema,
  GfeProvidedPayloadSchema,
  HashAlgorithmSchema,
  HumanApprovalRecordSchema,
  InspectionCompletedPayloadSchema,
  InspectionOrderedPayloadSchema,
  IntegrityAlertItemSchema,
  IntegrityAlertTypeSchema,
  IntegrityAlertsQuerySchema,
  IntegrityAlertsResponseSchema,
  InterestRateBasisPointsSchema,
  KnownChainIdSchema,
  LoanCaseActivatedPayloadSchema,
  LoanCaseAuditSchema,
  LoanCaseCreatedPayloadSchema,
  LoanCaseEventCategorySchema,
  LoanCaseEventSchema,
  LoanCaseListResponseSchema,
  LoanCaseQuerySchema,
  LoanCaseRefSchema,
  LoanCaseSchema,
  LoanCaseStatusSchema,
  LoanCaseVerificationCompletedPayloadSchema,
  LoanCaseVerificationRequestedPayloadSchema,
  LoanCaseWithEventsSchema,
  LoanClosedPayloadSchema,
  LoanFundedPayloadSchema,
  LoanTypeSchema,
  MerkleBatchSchema,
  MonetaryAmountSchema,
  NoteAddedPayloadSchema,
  OrderAppraisalApiPulledPayloadSchema,
  OrderCreditApiPulledPayloadSchema,
  OrderCreditUpdatedPayloadSchema,
  OrderFloodCertApiPulledPayloadSchema,
  OrderPmiApiPulledPayloadSchema,
  OrderTitleApiPulledPayloadSchema,
  PaginatedResponseSchema,
  PaginationQuerySchema,
  PaymentReceivedPayloadSchema,
  PaymentSchema,
  PaymentsListResponseSchema,
  PendingSignatureSchema,
  PrequalificationCompletedPayloadSchema,
  PublicAnchorStatusSchema,
  PublicBatchDetailSchema,
  PublicBatchSchema,
  PublicBulkVerifyResultSchema,
  PublicBulkVerifySchema,
  PublicCertificateMetadataSchema,
  PublicCertificateRequestSchema,
  PublicChainStatusSchema,
  PublicCsvReportRequestSchema,
  PublicEventDetailSchema,
  PublicEventSchema,
  PublicIntegrityAlertSchema,
  PublicIntegrityAlertTypeSchema,
  PublicIntegrityAlertsResponseSchema,
  PublicLoanAuditSummarySchema,
  PublicLoanRespaSchema,
  PublicProofSchema,
  PublicProofVerifyResultSchema,
  PublicProofVerifySchema,
  PublicSystemHealthSchema,
  RESPAServiceCodeSchema,
  RESPA_SERVICE_NAMES,
  RecordPaymentSchema,
  RespaServiceStatusSchema,
  RuncapApiPulledPayloadSchema,
  SetUserOfRecordSchema,
  SignaturesQuerySchema,
  SigningDepartmentSchema,
  SigningPolicyIdSchema,
  SigningStatusSchema,
  SigningWorkflowResultSchema,
  StateCodeSchema,
  StatusChangedPayloadSchema,
  SubmitApprovalSchema,
  SuccessResponseSchema,
  TermMonthsSchema,
  TilaProvidedPayloadSchema,
  TxHashSchema,
  UpdateLoanCaseStatusSchema,
  VerificationResultSchema,
  VerificationStatusSchema,
  VerifyByHashSchema,
  VerifyEventSchema,
  VodCompletedPayloadSchema,
  VodInitiatedPayloadSchema,
  VoeCompletedPayloadSchema,
  VoeInitiatedPayloadSchema,
  ZipCodeSchema,
  apiContract,
  approvalsContract,
  categorizeEventByRESPA,
  createIntegrationActor,
  createSystemActor,
  createUserActor,
  eventsContract,
  getActorTypeDescription,
  getEventTypesForRESPACode,
  getRESPAServiceCode,
  getRESPAServiceName,
  healthContract,
  loanCaseContract,
  publicVerificationContract,
  verificationContract
};
//# sourceMappingURL=index.js.map